import React, { useEffect, useRef, useState } from 'react';
// import AzureMapsService, { MapLocation, MapConfig } from '../services/azureMapsService';

interface MapLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
  businessType?: string;
  verificationStatus?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
}

interface SellerMapProps {
  locations?: MapLocation[];
  onLocationSelect?: (location: MapLocation) => void;
  height?: string;
  initialCenter?: [number, number];
  initialZoom?: number;
  className?: string;
}

interface MapFilters {
  businessType: string;
  verificationStatus: string;
  searchTerm: string;
}

const SellerMap: React.FC<SellerMapProps> = ({
  locations = [],
  onLocationSelect,
  height = '400px',
  className = ''
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [filters, setFilters] = useState<MapFilters>({
    businessType: '',
    verificationStatus: '',
    searchTerm: ''
  });

  // Business type options
  const businessTypes = [
    'Honey Producer',
    'Equipment Supplier',
    'Queen Breeder',
    'Pollination Services',
    'Bee Removal',
    'Education/Consulting'
  ];

  // Mock data for development
  const mockLocations: MapLocation[] = [
    {
      id: '1',
      name: 'Georgia Honey Farms',
      lat: 33.7490,
      lng: -84.3880,
      businessType: 'Honey Producer',
      verificationStatus: 'approved',
      address: '123 Peachtree St',
      city: 'Atlanta',
      state: 'GA',
      zipCode: '30309',
      phone: '(404) 555-0123',
      email: 'info@georgiahoney.com',
      description: 'Premium wildflower honey and bee products'
    },
    {
      id: '2',
      name: 'Carolina Queen Bees',
      lat: 35.2271,
      lng: -80.8431,
      businessType: 'Queen Breeder',
      verificationStatus: 'approved',
      address: '456 Queen St',
      city: 'Charlotte',
      state: 'NC',
      zipCode: '28202',
      phone: '(704) 555-0124',
      email: 'queens@carolina.com',
      description: 'High-quality Italian and Carniolan queens'
    },
    {
      id: '3',
      name: 'Alabama Bee Supply',
      lat: 32.3617,
      lng: -86.2792,
      businessType: 'Equipment Supplier',
      verificationStatus: 'pending',
      address: '789 Bee Ave',
      city: 'Montgomery',
      state: 'AL',
      zipCode: '36104',
      phone: '(334) 555-0125',
      email: 'supply@alabamabee.com',
      description: 'Complete beekeeping equipment and supplies'
    }
  ];

  useEffect(() => {
    // Simulate map initialization
    setTimeout(() => {
      setIsMapReady(true);
    }, 1000);
  }, []);

  const handleFilterChange = (filterType: keyof MapFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      businessType: '',
      verificationStatus: '',
      searchTerm: ''
    });
  };

  const handleSearch = async () => {
    // Placeholder for search functionality
    console.log('Search for:', filters.searchTerm);
  };

  const filteredLocations = (locations.length > 0 ? locations : mockLocations).filter(location => {
    if (filters.businessType && location.businessType !== filters.businessType) {
      return false;
    }
    if (filters.verificationStatus && location.verificationStatus !== filters.verificationStatus) {
      return false;
    }
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return (
        location.name.toLowerCase().includes(searchLower) ||
        location.city?.toLowerCase().includes(searchLower) ||
        location.state?.toLowerCase().includes(searchLower) ||
        location.zipCode?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className={`seller-map-container ${className}`}>
      {/* Map Controls */}
      <div className="map-controls bg-white p-4 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Location
            </label>
            <div className="flex">
              <input
                type="text"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="City, address, or zip code"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                🔍
              </button>
            </div>
          </div>

          {/* Business Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Type
            </label>
            <select
              value={filters.businessType}
              onChange={(e) => handleFilterChange('businessType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              {businessTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Verification Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.verificationStatus}
              onChange={(e) => handleFilterChange('verificationStatus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="approved">✓ Verified</option>
              <option value="pending">⏳ Pending</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredLocations.length} beekeepers and suppliers
        </div>
      </div>

      {/* Map Container */}
      <div className="map-wrapper relative">
        <div 
          id="seller-map" 
          ref={mapRef}
          style={{ height }}
          className="w-full bg-gray-100 border border-gray-300 rounded-md"
        >
          {/* Placeholder Map Content */}
          {isMapReady ? (
            <div className="h-full flex flex-col">
              <div className="flex-1 relative overflow-hidden">
                {/* Mock Map Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-100 to-blue-100">
                  <div className="absolute inset-0 opacity-20">
                    <svg viewBox="0 0 400 300" className="w-full h-full">
                      {/* Mock geographic features */}
                      <path d="M50,50 Q200,20 350,80 Q320,150 280,200 Q200,250 120,220 Q80,150 50,50" 
                            fill="#34d399" opacity="0.3" />
                      <path d="M100,80 Q250,60 320,120 Q300,180 250,220 Q180,240 130,200 Q90,140 100,80" 
                            fill="#60a5fa" opacity="0.2" />
                    </svg>
                  </div>
                </div>
                
                {/* Mock Location Markers */}
                {filteredLocations.map((location, index) => (
                  <div
                    key={location.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    style={{
                      left: `${20 + (index * 25) % 60}%`,
                      top: `${30 + (index * 20) % 40}%`
                    }}
                    onClick={() => onLocationSelect?.(location)}
                    title={`${location.name} - ${location.city}, ${location.state}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold ${
                      location.verificationStatus === 'approved' ? 'bg-blue-500' : 'bg-yellow-500'
                    }`}>
                      🐝
                    </div>
                    <div className="mt-1 bg-white px-2 py-1 rounded shadow-md text-xs font-medium whitespace-nowrap">
                      {location.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map Legend */}
      <div className="map-legend bg-gray-50 p-3 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-6 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span>Verified Sellers</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
            <span>Pending Verification</span>
          </div>
        </div>
      </div>

      {/* Location List */}
      {filteredLocations.length > 0 && (
        <div className="location-list bg-white border-t border-gray-200 max-h-64 overflow-y-auto">
          {filteredLocations.map((location) => (
            <div
              key={location.id}
              className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
              onClick={() => onLocationSelect?.(location)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{location.name}</h4>
                  <p className="text-sm text-gray-600">{location.businessType}</p>
                  <p className="text-sm text-gray-500">
                    {location.city}, {location.state} {location.zipCode}
                  </p>
                  {location.description && (
                    <p className="text-sm text-gray-500 mt-1">{location.description}</p>
                  )}
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  location.verificationStatus === 'approved' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {location.verificationStatus === 'approved' ? '✓ Verified' : '⏳ Pending'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SellerMap;
